import {
  ERROR_AMOUNT_SOURCES,
  buildOtherDeductionOccurrence,
  checkOtherDeductionOccurrences,
  findOtherDeduction,
} from "score-common";

// #3152 - scribe-fe's side of recording a per-occurrence catalog
// `otherDeductions` entry (engine: #1630, packages/score-common/src/utils/
// other-deductions.js). Entries are NEVER hand-built here: planOtherDeduction
// goes through buildOtherDeductionOccurrence, then re-judges the would-be
// errors[] with checkOtherDeductionOccurrences, the SAME check score-api runs
// on create/PUT/resolve. A server 400 on a sheet PUT is quarantined by
// sync.js (#2812) and the op removed, so the only place a scribe can be told
// a cap refusal in words is HERE, before the entry ever reaches the outbox.
// (planOtherDeduction also reports notEnriched, a Test row with no table, but
// the UI never offers a deduction to such a test, so ScoringForm cannot reach
// it: card 3155 owns surfacing it from the server.)

/**
 * The catalog entries a scribe may record on this test. A `cumulative: true`
 * entry is a ladder tier that belongs to errorRule (findOtherDeduction refuses
 * it), so it is not offered either.
 *
 * @param {object|null} test
 * @returns {Array<object>}
 */
export function recordableOtherDeductions(test) {
  const list = Array.isArray(test?.otherDeductions) ? test.otherDeductions : [];
  return list.filter(
    (d) => typeof d?.label === "string" && d.label !== "" && findOtherDeduction(test, d.label),
  );
}

/** "-4 pt" / "-0.5%", the unit the catalog entry carries. */
export function otherDeductionAmountText(def) {
  return def?.unit === "percent" ? `−${def.value}%` : `−${def.value} pt`;
}

/**
 * True when the catalog entry says the charge is not repeated for the same
 * fault ("... but not for each recurrence of the same movement", USDF
 * Forbidden Movement). The engine only caps by maxOccurrences and cannot judge
 * what a recurrence is, so the scribe is told at the point of recording and a
 * second one needs a deliberate confirmation. Keyed off the catalog's own
 * `note` text, not a hard-coded label, so no federation branch lives here.
 */
export function isRecurrenceExempt(def) {
  return typeof def?.note === "string" && /recurrence/i.test(def.note);
}

/** How many occurrences of `label` the sheet already carries. */
export function recordedOtherDeductionCount(errors, label) {
  return (errors ?? []).filter(
    (e) => e?.amountSource === ERROR_AMOUNT_SOURCES.OTHER_DEDUCTION && e.label === label,
  ).length;
}

/**
 * Decide what recording one occurrence of `label` would do.
 *
 * @param {object} args
 * @param {object|null} args.test - catalog Test row (carries otherDeductions once enriched)
 * @param {string} args.label
 * @param {Array<object>} args.errors - the sheet's current errors[]
 * @param {{ id: string, movementNumber?: number|null }} args.occurrence
 * @returns {{ ok: true, entry: object, recurrenceExempt: boolean, alreadyRecorded: boolean }
 *   | { ok: false, reason: "notEnriched" | "unknown" | "mismatch" | "cap", label: string, max: number|null }}
 */
export function planOtherDeduction({ test, label, errors, occurrence }) {
  const fail = (reason) => ({
    ok: false,
    reason,
    label,
    max: findOtherDeduction(test, label)?.maxOccurrences ?? null,
  });
  const built = buildOtherDeductionOccurrence(test, label, errors, occurrence);
  if (!built.ok) {
    const noTable = !Array.isArray(test?.otherDeductions) || test.otherDeductions.length === 0;
    return fail(noTable ? "notEnriched" : built.reason);
  }
  // Same judgement score-api makes, run on the exact errors[] the save would
  // carry, so anything the server would 400 on is refused here instead.
  const problems = checkOtherDeductionOccurrences(test, [...(errors ?? []), built.entry], errors ?? []);
  if (problems.length > 0) return fail(problems[0].reason);
  const def = findOtherDeduction(test, label);
  return {
    ok: true,
    entry: built.entry,
    recurrenceExempt: isRecurrenceExempt(def),
    alreadyRecorded: recordedOtherDeductionCount(errors, label) > 0,
  };
}
